/**
 * The View builder's working tree (design/reboot/views.js ported): editable nodes with stable keys, the
 * field / operator vocabulary of `contracts/rules.ts` as the editor offers it, defaults per field, the
 * prototype's completeness check, and conversion to and from the engine's `GroupNode`.
 */

import type { ConditionNode, GroupNode, NumberOperator, QueryNode, SourceKind } from '@digester/core';

export type ViewField = 'folder' | 'source' | 'topic' | 'title' | 'body' | 'tag' | 'read' | 'topic-score' | 'hours' | 'author' | 'source-type' | 'image';

export const VIEW_FIELDS: ReadonlyArray<readonly [ViewField, string]> = [
  ['folder', 'Folder'], ['source', 'Source'], ['topic', 'Topic'], ['title', 'Title'],
  ['body', 'Body'], ['tag', 'Tag'], ['read', 'Read status'], ['topic-score', 'Topic score'],
  ['hours', 'Time in folder'], ['author', 'Author'], ['source-type', 'Source type'], ['image', 'Image'],
];

export const SET_FIELDS: ReadonlySet<ViewField> = new Set(['folder', 'source', 'topic', 'tag', 'source-type']);
const TEXT_FIELDS: ReadonlySet<ViewField> = new Set(['title', 'author']);

export function operatorsFor(field: ViewField): ReadonlyArray<readonly [string, string]> {
  if (SET_FIELDS.has(field)) return [['in', 'is any of'], ['not-in', 'is none of'], ...(field === 'topic' || field === 'tag' ? ([['all', 'includes all']] as const) : [])];
  if (TEXT_FIELDS.has(field)) return [['contains', 'contains'], ['not-contains', 'does not contain'], ['equals', 'is exactly'], ['not-equals', 'is not'], ['starts-with', 'starts with'], ['ends-with', 'ends with']];
  // Body matches word phrases (engine.md "Text matching"): contains / does not contain.
  if (field === 'body') return [['contains', 'contains'], ['not-contains', 'does not contain']];
  if (field === 'topic-score' || field === 'hours') return [['gte', 'at least'], ['gt', 'more than'], ['lte', 'at most'], ['lt', 'less than'], ['eq', 'equals']];
  return [['is', 'is']];
}

export interface EditCondition {
  key: string;
  type: 'condition';
  field: ViewField;
  operator: string;
  values: string[];
  value: string;
  topic: string;
  /** A score condition's model (models.md §8.1): '' = the shown scores. */
  model: string;
  unit: 'hours' | 'days';
}

export interface EditGroup {
  key: string;
  type: 'group';
  operator: 'all' | 'any';
  children: EditNode[];
}

export type EditNode = EditGroup | EditCondition;

let counter = 0;
export const nextKey = (): string => `vb${++counter}`;

export function newCondition(field: ViewField = 'folder'): EditCondition {
  return {
    key: nextKey(),
    type: 'condition',
    field,
    operator: operatorsFor(field)[0]?.[0] ?? 'is',
    values: [],
    value: field === 'read' ? 'unread' : field === 'image' ? 'yes' : field === 'topic-score' ? '70' : field === 'hours' ? '24' : '',
    topic: '',
    model: '',
    unit: 'hours',
  };
}

export function newGroup(children: EditNode[] = []): EditGroup {
  return { key: nextKey(), type: 'group', operator: 'all', children };
}

export function fromQuery(node: QueryNode): EditNode {
  if (node.type === 'group') return { key: nextKey(), type: 'group', operator: node.operator, children: node.children.map(fromQuery) };
  // A view has no `script` (validateQuery rejects it, models.md §8.2): such a tree never reaches the view editor.
  if (node.field === 'script') return newGroup();
  // A score condition may name the model it tests (models.md §8.1); the editor keeps it (a view compiles it to
  // an EXISTS over message_scores).
  const base = newCondition(node.field === 'best-score' ? 'topic-score' : node.field);
  switch (node.field) {
    case 'folder':
    case 'source':
    case 'source-type':
    case 'topic':
    case 'tag':
      return { ...base, operator: node.operator, values: [...node.values] };
    case 'title':
    case 'author':
    case 'body':
      return { ...base, operator: node.operator, value: node.value };
    case 'read':
    case 'image':
      return { ...base, operator: 'is', value: node.value };
    case 'topic-score':
      return { ...base, operator: node.operator, value: String(node.value), topic: node.topic ?? '', model: node.model ?? '' };
    case 'best-score':
      return { ...base, operator: node.operator, value: String(node.value), model: node.model ?? '' };
    case 'hours':
      return { ...base, operator: node.operator, value: String(node.value), unit: node.unit };
  }
}

function toCondition(c: EditCondition): ConditionNode {
  const num = (): number => (c.value.trim() === '' ? Number.NaN : Number(c.value));
  switch (c.field) {
    case 'folder':
      return { type: 'condition', field: 'folder', operator: c.operator === 'not-in' ? 'not-in' : 'in', values: c.values as never };
    case 'source':
      return { type: 'condition', field: 'source', operator: c.operator === 'not-in' ? 'not-in' : 'in', values: c.values as never };
    case 'source-type':
      return { type: 'condition', field: 'source-type', operator: c.operator === 'not-in' ? 'not-in' : 'in', values: c.values as SourceKind[] };
    case 'topic':
      return { type: 'condition', field: 'topic', operator: c.operator as 'in' | 'not-in' | 'all', values: c.values as never };
    case 'tag':
      return { type: 'condition', field: 'tag', operator: c.operator as 'in' | 'not-in' | 'all', values: c.values as never };
    case 'title':
    case 'author':
      return { type: 'condition', field: c.field, operator: c.operator as 'contains', value: c.value };
    case 'body':
      return { type: 'condition', field: 'body', operator: c.operator === 'not-contains' ? 'not-contains' : 'contains', value: c.value };
    case 'read':
      return { type: 'condition', field: 'read', operator: 'is', value: c.value === 'read' ? 'read' : 'unread' };
    case 'image':
      return { type: 'condition', field: 'image', operator: 'is', value: c.value === 'no' ? 'no' : 'yes' };
    case 'topic-score':
      return {
        type: 'condition',
        field: 'topic-score',
        operator: c.operator as NumberOperator,
        value: num() as never,
        ...(c.topic ? { topic: c.topic as never } : {}),
        ...(c.model ? { model: c.model as never } : {}),
      };
    case 'hours':
      return { type: 'condition', field: 'hours', operator: c.operator as NumberOperator, value: num(), unit: c.unit };
  }
}

export function toQuery(node: EditGroup): GroupNode {
  return { type: 'group', operator: node.operator, children: node.children.map((c) => (c.type === 'group' ? toQuery(c) : toCondition(c))) };
}

/** The prototype's save check: nested groups non-empty, set conditions have values, text non-blank, numbers set. */
export function isComplete(root: EditGroup): boolean {
  let ok = true;
  const walk = (n: EditNode, isRoot: boolean): void => {
    if (n.type === 'group') {
      if (!isRoot && n.children.length === 0) ok = false;
      n.children.forEach((c) => walk(c, false));
      return;
    }
    if (SET_FIELDS.has(n.field) && n.values.length === 0) ok = false;
    if ((TEXT_FIELDS.has(n.field) || n.field === 'body') && !n.value.trim()) ok = false;
    if ((n.field === 'topic-score' || n.field === 'hours') && (n.value.trim() === '' || !Number.isFinite(Number(n.value)))) ok = false;
  };
  walk(root, true);
  return ok;
}

/** Immutable update helpers over the tree. */
export function mapNode(root: EditGroup, key: string, fn: (n: EditNode) => EditNode): EditGroup {
  const visit = (n: EditNode): EditNode => {
    if (n.key === key) return fn(n);
    return n.type === 'group' ? { ...n, children: n.children.map(visit) } : n;
  };
  return visit(root) as EditGroup;
}

export function removeNode(root: EditGroup, key: string): EditGroup {
  const visit = (g: EditGroup): EditGroup => ({ ...g, children: g.children.filter((c) => c.key !== key).map((c) => (c.type === 'group' ? visit(c) : c)) });
  return visit(root);
}
